
import {
    IChatService,
    ChatEventHandler,
    MessageEvent,
    MessageStatus,
    MessageContentType,
    MessageDirection,
    ChatEvent,
    ChatEventType,
    ChatState,
    IStorage,
    UpdateState,
    SendTypingServiceParams,
    Conversation,
    Participant,
    User,
    Presence,
    UserStatus,
    ChatMessage,
    TypedMessageContent,
    Sender,
    SenderType, // <-- CORRECTED: Imported the required parameter type
} from "@chatscope/use-chat";

// UPDATED IMPORT PATH to match your specified file structure
import { chatscopeApi,  RawConversationOptionalIdMessagesTS, } from './useChatSlice';
import { RawMessage, CreateConversationFromRaw, RawConversation, RawConversationOptionalId, RawConversationMember } from "./chatSelectors";
import { isFetchBaseQueryError } from "./profilesSlice";
import { chatApi } from "./ChatSlice";
import { printError } from "../app/utils";
import { ChatStateUpdate, PollingChatDataService } from "./pollingChatDataService";
import { getUnreadMessagesAllConverstations, pollingChatDataService } from "../components/messaging/ChatScopeUseChat";
import { store } from "../app/store";
import { setLastRefreshUnreadMessageCount } from "./dialogSlice";
import { addTrace } from "../app/debugTrace";



export function arraysHaveSameDistinctElements(a: string[], b: string[]) {
    const as = new Set(a);
    const bs = new Set(b);
    if (as.size !== bs.size) return false;
    for (const v of as) {
        if (!bs.has(v)) return false;
    }
    return true;
}
/**
 * Creates a type-safe ChatMessage instance from raw data by mapping rawMsg.body 
 * into the content payload required by the specified MessageContentType.
 * * @param rawMsg The raw data object received from the API/DB.
 * @param currentUserId The ID of the current user for direction calculation.
 * @param contentTypeValue The desired MessageContentType (MessageContentType.TextPlain or TextHtml).
 * @returns A ChatMessage instance strictly typed by T.
 */
export const CreateMessageFromRaw =
    <T extends MessageContentType>(
        rawMsg: RawMessage,
        currentUserId: number,
        contentTypeValue: T
    ): ChatMessage<T> => {
        try {
            // The variable to hold the final, correctly shaped content object
            let contentObject: TypedMessageContent<T>;

            // 1. Internal Mapping: Construct the content payload based on the message type
            switch (contentTypeValue) {
                // Case 1: Plain Text (requires a 'text' property)
                case MessageContentType.TextPlain:
                    // Assertion: { text: string } satisfies TypedMessageContent<TextPlain>
                    contentObject = { body: rawMsg.body } as TypedMessageContent<T>;
                    break;

                // Case 2: HTML Content (requires an 'html' property)
                case MessageContentType.TextHtml:
                    // Assertion: { html: string } satisfies TypedMessageContent<TextHtml>
                    contentObject = { body: rawMsg.body } as TypedMessageContent<T>;
                    break;

                default:
                    // If any other type is passed, we throw an error for safety.
                    throw new Error(`Content type ${contentTypeValue} is not supported by CreateMessageFromRaw for simple body mapping.`);
            }

            // 2. Create the ChatMessage instance
            const cm = new ChatMessage({
                id: rawMsg.id.toString(),
                status: (rawMsg.is_read || rawMsg.metadata[currentUserId.toString()] || rawMsg.originator_id === currentUserId) ? MessageStatus.Seen : MessageStatus.Sent,//MessageStatus.DeliveredToDevice,
                //status: MessageStatus.Sent,//rawMsg.originator_id === currentUserId        ? MessageStatus.Sent        : MessageStatus.DeliveredToCloud, // ensures unread increment
                contentType: contentTypeValue,
                sender: new Sender({
                    id: rawMsg.originator_id?.toString(),
                    type: SenderType.User
                }),
                direction: rawMsg.originator_id === currentUserId
                    ? MessageDirection.Outgoing
                    : MessageDirection.Incoming,
                content: contentObject,
                createdTime: new Date(rawMsg.sent_at),
                ...rawMsg.metadata ? { data: rawMsg.metadata } : {}
            });

            return cm;
        } catch (err) {
            throw err
        }
    }




type EventHandlers = {
    onMessage: ChatEventHandler<
        ChatEventType.Message,
        ChatEvent<ChatEventType.Message>
    >;
    onConnectionStateChanged: ChatEventHandler<
        ChatEventType.ConnectionStateChanged,
        ChatEvent<ChatEventType.ConnectionStateChanged>
    >;
    onUserConnected: ChatEventHandler<
        ChatEventType.UserConnected,
        ChatEvent<ChatEventType.UserConnected>
    >;
    onUserDisconnected: ChatEventHandler<
        ChatEventType.UserDisconnected,
        ChatEvent<ChatEventType.UserDisconnected>
    >;
    onUserPresenceChanged: ChatEventHandler<
        ChatEventType.UserPresenceChanged,
        ChatEvent<ChatEventType.UserPresenceChanged>
    >;
    onUserTyping: ChatEventHandler<
        ChatEventType.UserTyping,
        ChatEvent<ChatEventType.UserTyping>
    >;
    [key: string]: any;
};


// --- Define the types expected by the service from the RTK Query slice ---
// NOTE: These are placeholders. You should replace them with your actual Redux types.
type RootState = any;
type AppDispatch = any;

/**
 * Selects the initial timestamp from the RTK Query cache for the specific userId.
 * This looks up the result of the 'getChatState' query in the Redux store.
 */
const selectInitialTimestampFromCache = (getState: () => RootState, /*conversationId: number,*/): any => {
    const state = getState();
    // The cache key includes the stringified argument (the userId)
    //const parmString = JSON.stringify(parmObject)
    //const cacheKey = `getChatState(${parmString})`;// `getChatState({"userId":${userId}})`;
    const cacheKey = Object.keys(state.chatscopeApi?.queries).filter((key) => key.startsWith('getChatState')).pop();
    const cacheEntry = cacheKey ? state.chatscopeApi.queries[cacheKey] : undefined;

    if (cacheEntry && cacheEntry.status === 'fulfilled' && cacheEntry.data) {
        // We use the renamed property: lastMessageTimestamp
        return { ts: cacheEntry?.data?.length ? (cacheEntry.data[0].retrieved_at_ts || null) : null, status: cacheEntry?.status, data: cacheEntry.data };
    }
    return { ts: null, status: cacheEntry?.status }
};
const environmentREACT_APP_ENV: string = process.env.REACT_APP_ENV || 'development'
// --- HELPER TYPES & CONSTANTS ---
export const POLLING_INTERVAL_MS = (environmentREACT_APP_ENV === 'development') ? 10000 : environmentREACT_APP_ENV === 'test' ? 10000 : environmentREACT_APP_ENV === 'prod' ? 5000 : 5000; // 30 seconds

// // Transformation utility 
// const transformToChatscopeFormat = (rawMsg: any): any => ({
//     id: rawMsg.id,
//     message: rawMsg.body,
//     sender: rawMsg.originator_id,
//     direction: MessageDirection.Incoming,
//     sentTime: rawMsg.sent_at,
//     // Store the raw timestamp in metadata for internal tracking
// });

export type ServiceState = {
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    isInitialized: boolean
    error?: Error;
    data?: any,
};
// --- CORE SERVICE IMPLEMENTATION ---

export class ChatService implements IChatService {
    private _chatStorage: IStorage;
    private updateState: UpdateState;
    private dispatch: AppDispatch;
    private getState: () => RootState;
    private userId: number = 0; // Stored user ID

    private onSend: ((message: RawMessage, convo: Conversation, userId: number) => void) | undefined
    //private conversationId: number = 0;
    private setServiceState: (serviceState: ServiceState) => void = (serviceState: ServiceState) => {
        this.serviceState = serviceState;
        // setServiceState(serviceState)
    }
    private serviceState: ServiceState = { isInitialized: false, isLoading: false, isError: false, isSuccess: false }
    // Internal state to track the latest timestamp of the message successfully processed by the client.
    private latestHandledTimestamp: string = '1970-01-01T00:00:00.000Z';

    private resetLatestHandledTimestamp = () => this.latestHandledTimestamp = '1970-01-01T00:00:00.000Z'

    private pollingIntervalId: number | null = null;
    eventHandlers: EventHandlers = {
        onMessage: () => { },
        onConnectionStateChanged: () => { },
        onUserConnected: () => { },
        onUserDisconnected: () => { },
        onUserPresenceChanged: () => { },
        onUserTyping: () => { },
    };
    private resetPoollingForUser: (userId: number) => void = () => { }
    private deleteSubscription: () => void = () => { }
    private detachPollingStateListener: () => void = () => { }
    private populateStateMethod
    private pollingChatDataService
    // CONSTRUCTOR: Now requires the userId
    constructor(
        chatStorage: IStorage,
        update: UpdateState,
        dispatch: AppDispatch,
        getState: () => RootState,
        // REQUIRED: ID of the currently authenticated user
        userId: number,
        pollingChatDataService: PollingChatDataService,
        //conversationId: number
        //setServiceState: (serviceState: ServiceState) => void
        onSend?: (message: RawMessage, convo: Conversation, userId: number) => void,
        conversationFilter?: (convoId: number) => boolean
    ) {
        this._chatStorage = chatStorage;
        this.updateState = update;
        this.dispatch = dispatch;
        this.getState = getState;
        this.userId = userId; // Store userId
        this.onSend = onSend
        this.pollingChatDataService = pollingChatDataService
        //subscribe again to override the previous storage only subscription
        var { deleteSubscription, resetForUser: resetPoollingForUser } = pollingChatDataService.subscribe(this._chatStorage, (update: ChatStateUpdate) => {
            this.processConvoAnMessageStateUpdate(update) //calls this.populateStateMethod
            addTrace('ChatService:before-updateState', '');
            this.updateState()
            addTrace('ChatService:after-updateState', '');
        }, userId)

        // Forward every polling-service lifecycle transition into this
        // ChatService's setServiceState — including isLoading: true at
        // fetch start, which previously had no path to the UI.
        this.detachPollingStateListener = pollingChatDataService.attachSetServiceStateFunc((s) => {
            this.setServiceState({ ...s });
        })

        this.setServiceState(pollingChatDataService.getServiceState())
        this.populateStateMethod = GetPopulateStateMethod(this._chatStorage,
            {
                convoHook: (rawConvo) => {
                    const ret =  this.matchConversation(rawConvo)
                    return ret
                },
                newMessageHook: (message, matchedConvoId) => {
                    //if ( this._chatStorage.getState().messages[rawConvo.id.toString()])
                    //console.info("new message", message)
                    const inActiveConversation = this._chatStorage.getState().activeConversation?.id === matchedConvoId 
                    if (inActiveConversation) {
                        message.status = MessageStatus.Seen
                    }
                    //TODO:improve by sending the sentMessagesIds array to the SQL function not to even retrieve
                    if (    //rawMsg.originator_id === this.userId && // to only add messages to the ??????????
                        !this.sentMessagesIds[message.id]) {//[rawMsg.id]) {
                        this.eventHandlers.onMessage(new MessageEvent({ message, conversationId: matchedConvoId?.toString()/*rawConvo.id.toString()*/ }));
                    }
                    const state = this._chatStorage.getState()
                    if (Number(message.sender.id) /* rawMsg.originator_id */ !== this.userId) {
                        if (inActiveConversation) {
                            // const setReadMessageThunk = chatscopeApi.endpoints.setReadMessage.initiate({ messageId: Number(message.id), userId: this.userId },);
                            // store.dispatch(setReadMessageThunk).unwrap().catch((err) => {
                            //     console.error('setReadMessage failed', err);
                            // }); // marking messages read one by one // TODO update in the SQL function
                            // detach AFTER the request has been sent
                            //
                            //store.dispatch(markMessageRead(Number(message.id), this.userId));
                            pollingChatDataService.markMessageRead( Number(message.id))
                        }
                    }
                    //store.dispatch(setLastRefreshUnreadMessageCount(getUnreadMessagesAllConverstations()??0))
                }
            },
            conversationFilter)
        this.resetPoollingForUser = resetPoollingForUser
        this.deleteSubscription = deleteSubscription

        // 1. Initiate the RTK Query to fetch the initial state using the userId
        if (this.userId) {
            const gloabalState = getState()
            const convoId = this._chatStorage.getState().activeConversation
            //queueMicrotask(() => this.getConvoAnMessageState({ userId: this.userId, }));
        }
    }

    public getUserId() {
        return this.userId
    }
    public resetForUser(userId: number) {
        //this.dispose()
        this.userId = userId
        if (this.userId) {
            const gloabalState = this.getState()
            const convoId = this._chatStorage.getState().activeConversation
            // //this.getConvoAnMessageState({ userId: this.userId, })
            // queueMicrotask(() => this.getConvoAnMessageState({ userId: this.userId, }));//timestamp default doesnt even matter, since not passing the first time around
            this.resetPoollingForUser(userId)
        }

    }

    public getServiceState: () => ServiceState = () => { return { ...this.serviceState } }
    // Transparent forwarder: every state transition reaches the UI
    // subscriber. UI subscribers apply their own newMessagesOnly filter.
    public attachSetServiceStateFunc(setServiceState: (serviceState: ServiceState) => void) {
        this.setServiceState = (serviceState: ServiceState) => {
            this.serviceState = serviceState;
            setServiceState(serviceState)
        }
    }
    public getLastMessageForConversation(conversationId: string) {
        const messageGroups = this._chatStorage.getState().messages[conversationId] ?? [];
        const lastMessageGroupMessages = messageGroups.length ? messageGroups[messageGroups.length - 1].messages : undefined
        const lastMessage = lastMessageGroupMessages?.length ? lastMessageGroupMessages[lastMessageGroupMessages.length - 1] : undefined
        return lastMessage
    }
    public getLastActivityMessageForConversation(conversationId: string, activity: string) {
        const messageGroups = this._chatStorage.getState().messages[conversationId] ?? [];
        for (const messageGroup of messageGroups) {
            messageGroup.messages.findLast(m => (m.data as any)?.activity === activity)
        }
        const lastMessageGroupMessages = messageGroups.length ? messageGroups[messageGroups.length - 1].messages : undefined
        const lastMessage = lastMessageGroupMessages?.length ? lastMessageGroupMessages[lastMessageGroupMessages.length - 1] : undefined
        return lastMessage
    }
    private getMatchUpdateSavedIdConversationBySavedIdOrMembers: (arg: { chatName: string, isNamedChat: boolean, memberIds: string[], savedId: number | undefined }) => any = ({ chatName, memberIds, isNamedChat, savedId }) => {
        const conversations = this._chatStorage.getState().conversations
        const findMatchingConvoRet = conversations.filter(c => {
            if (savedId && c.data?.savedId && c.data?.savedId === savedId) {
                return true
            }
            const convoMembers = c.participants.map(m => m.id)
            const ret = arraysHaveSameDistinctElements(convoMembers, memberIds)
            // ignoring chat name for direct chats = !isNamedChat
            const isMatch = ret && ((!isNamedChat && !c.data.is_named_chat) || chatName === c?.description)
            if (isMatch && savedId) {
                c.data = { ...c.data, savedId, status: 'saved' }
                this._chatStorage.updateConversation(c)
            }
            return isMatch
        })
        return findMatchingConvoRet
    }

    // --- State Access ---
    public getLatestMessageTimestamp(): string {
        return this.latestHandledTimestamp;
    }

    private matchConversation = (rawConvo: RawConversationOptionalId) => {
        const memberIds = rawConvo.members.map((m: any) => m.id.toString())
        if (rawConvo?.id) { //first try to match by the id of convo in storage
            const [existingConvo] = this._chatStorage.getConversation(rawConvo.id.toString());
            if (existingConvo) {
                addTrace('matchConvo:by-id', `rawId=${rawConvo.id} matched existingId=${existingConvo.id} chat_name=${rawConvo.chat_name} is_named_chat=${rawConvo.is_named_chat} members=[${memberIds.join(',')}]`);
                return existingConvo
            }
        }
        const matchedByMembersRet = this.getMatchUpdateSavedIdConversationBySavedIdOrMembers({ chatName: rawConvo.chat_name, isNamedChat: rawConvo.is_named_chat, memberIds, savedId: rawConvo.id })
        if (matchedByMembersRet?.length === 1) {
            const m = matchedByMembersRet[0];
            const mMembers = m.participants.map((p: any) => p.id).join(',');
            addTrace('matchConvo:by-members', `rawId=${rawConvo.id} chat_name="${rawConvo.chat_name}" is_named_chat=${rawConvo.is_named_chat} rawMembers=[${memberIds.join(',')}] → matched existingId=${m.id} description="${m.description}" data.is_named_chat=${m.data?.is_named_chat} existingMembers=[${mMembers}]`);
            return matchedByMembersRet[0]
        }
        if (matchedByMembersRet?.length > 1) {
            addTrace('matchConvo:by-members-AMBIGUOUS', `rawId=${rawConvo.id} matchCount=${matchedByMembersRet.length} — returning undefined`);
        } else {
            addTrace('matchConvo:no-match', `rawId=${rawConvo.id} chat_name="${rawConvo.chat_name}" is_named_chat=${rawConvo.is_named_chat} members=[${memberIds.join(',')}]`);
        }
        return undefined
    }
    // RawConversation {
    //     id: string;
    //     chat_name: string;
    //     //last_message_text: string;
    //     //last_message_date: number; // Unix timestamp
    //     members: { id: number; name_public: string }[];
    //     is_named_chat: boolean,
    //     message_count: number,
    //     last_message_sender_name: string
    //     unread_message_count: number,
    //     metadata?: any
    //     // We assume the full list of messages is available in the conversation object
    //     //messages: RawMessage[];
    // }

    public matchOrAddNewTempDirectConversation: (rawConversation: RawConversationOptionalId) => Conversation = (rawConversation) => {
        //const { id, name_public } = rawConversation
        const existingConvo = this.matchConversation(rawConversation) //no savedId here
        if (existingConvo) {
            return existingConvo
        }

        var descriptionFromMemberNames: string
        var id: string

        switch (rawConversation.members.length) {
            case 0:
                return null
            case 1:
                descriptionFromMemberNames = rawConversation.chat_name
                id = rawConversation.members[0].id.toString()
                break
            default:
                const nonLoggedInMembers = rawConversation.members.filter((m: RawConversationMember) => m.id != this.userId)
                descriptionFromMemberNames = nonLoggedInMembers.map((m: RawConversationMember) => m.name_public).join(", ")
                id = nonLoggedInMembers.length ? nonLoggedInMembers[0].id.toString() : rawConversation.members[0].id.toString()
        }
        const description = rawConversation.is_named_chat ? rawConversation.chat_name ?? descriptionFromMemberNames : descriptionFromMemberNames ?? 'Conversation'
        const conversaton = new Conversation({
            id: id,//rawConversation.members[1].id.toString(), //id convo with the participant id
            //unreadCounter: unreadCount,
            description: description,//rawConversation.members[1].name_public,
            draft: "", // Placeholder
            participants: rawConversation.members.map(m => new Participant({ id: m.id.toString(), role: undefined })), // [new Participant({ id: id.toString(), role: undefined }), new Participant({ id: this.userId.toString(), role: undefined })],
            data: { status: 'unsaved', savedId: undefined, ...rawConversation.metadata ?? {} }
        });

        const convoAdded = this._chatStorage.addConversation(conversaton!);
        this.processConvoForUsers(rawConversation)
        return conversaton
    }


    private processConvoForUsers = (rawConvo: RawConversationOptionalId) => {
        rawConvo.members.forEach((m: any) => {
            const [user] = this._chatStorage.getUser(m.id.toString());
            if (!user) {
                const newUser = new User({
                    id: m.id.toString(),
                    presence: new Presence({ status: UserStatus.Available, description: "" }),
                    //firstName: "",
                    //lastName: "",
                    username: m.name_public,
                    avatar:
                        m.id === 0//this.userId
                            ? "https://www.polygonhealth.com/map/static/media/logo.5b654776c368b37500be.png"
                            : "https://chatscope.io/storybook/react/assets/emily-xzL8sDL2.svg",
                    bio: ""
                });
                this._chatStorage.addUser(newUser);
            }
        });
    }


    private sentMessagesIds: any = {}
    // --- IChatService Contract Methods ---
    async sendMessage({ message, conversationId }: { message: any; conversationId: string }) {
        try {
            const bodyAsText = new DOMParser().parseFromString(message.content.body, "text/html").body.textContent || "";
            message.content.body = bodyAsText
            const [convo, _] = this._chatStorage.getConversation(conversationId)
            //const receipent = convo?.participants.find(p => p.id !== this.userId.toString())
            var chatOrAddMessageRet
            if (convo?.data?.status === 'unsaved') {
                const addChatAndSendMessageObject = {
                    id: this.userId,
                    //userName?: string
                    messageToId: Number(convo.participants.find(p => p.id !== this.userId.toString())?.id),
                    sendInChat: true,
                    //sendEmail?: any
                    messageContent: message.content.body,
                    //messageChat?: string,
                    ...convo?.data?.spotlight ? { chatMetadata: { spotlight: true } } : {},//messageEmail?: string,
                    chatName: convo.description
                }
                const thunk = chatApi.endpoints.setChatUser.initiate(addChatAndSendMessageObject);
                chatOrAddMessageRet = await this.dispatch(thunk).unwrap();
                if (chatOrAddMessageRet.error) {
                    if (chatOrAddMessageRet.error instanceof Error) {
                        throw (chatOrAddMessageRet.error)
                    }
                    const errObj = new Error("Something went wrong");
                    throw errObj
                }
                convo.data = { ...convo.data, status: 'saved', savedId: chatOrAddMessageRet.conversationId }
                this._chatStorage.updateConversation(convo)
                this.sentMessagesIds[chatOrAddMessageRet?.message_id] = message.id
                //this._chatStorage.updateMessage(message)
                //return
            } else {
                const newRawMessage = {
                    body: message.content.body,
                    originator_id: Number(this.userId)/*this.userId*/,
                    conversation_id: convo?.data?.savedId ?? Number(conversationId),
                    direction: 'inbound',
                    makeMemberIfNot: true
                }  //sent_at: '',   is_read: false;
                const thunk = chatscopeApi.endpoints.addMessage.initiate(newRawMessage);
                chatOrAddMessageRet = await this.dispatch(thunk).unwrap();
                if (chatOrAddMessageRet?.error) {
                    if (chatOrAddMessageRet.error instanceof Error) {
                        throw (chatOrAddMessageRet.error)
                    }
                    const errObj = new Error("Something went wrong");
                    throw errObj
                }
                this.sentMessagesIds[chatOrAddMessageRet?.message_id] = message.id
                //message.id = addMessageRet?.message_id
                //this._chatStorage.updateMessage(message)
                //return message;
            }
            // Promote the optimistic Pending message to Sent — handleSend in
            // ChatScopeUseChat.tsx creates the message at status=Pending.
            // On failure (the catch below), we deliberately leave it at Pending
            // so the UI can visually distinguish a failed send from a confirmed one.
            message.status = MessageStatus.Sent;
            this._chatStorage.updateMessage(message)
            if (this.onSend) {
                this.onSend(chatOrAddMessageRet.savedMessage, convo!, this.userId)
            }

            return message;

        } catch (error) {
            console.error("[SERVICE] Message Failed:", error);
            // Leave message.status at MessageStatus.Pending and stamp data.failed=true.
            // The UI distinguishes:
            //   status===Pending && !data.failed → gray "Sending" (in flight)
            //   status===Pending &&  data.failed → red  "Not sent" (hard failure)
            //   status===Sent                    → no indicator (success)
            try {
                if (message?.id) {
                    (message as any).data = { ...((message as any).data ?? {}), failed: true };
                    this._chatStorage.updateMessage(message);
                    this.updateState();
                }
            } catch (e) {
                console.error('Failed to stamp message.data.failed:', e);
            }
            //throw new Error("Failed to send message over network.");
            return null;
        }
    }


    private async processConvoAnMessageStateUpdate(update: ChatStateUpdate) {
        const { rawChatStateData, newMessagesOnly, latestHandledTimestamp } = update
        const rawConvoIds = (rawChatStateData ?? []).map((c: any) => c?.id).join(',');
        addTrace('ChatService:process-start', `convoRows=${rawChatStateData?.length ?? 0} rawIds=[${rawConvoIds}] newMessagesOnly=${newMessagesOnly} isSuccess=${update.serviceState.isSuccess}`);
        if (update.serviceState.isSuccess) {
            const tPopStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
            const { conversationAddedCount, messagesAddedCount } = this.populateStateMethod(update)//this.processStateResult(update.rawChatStateData, update.newMessagesOnly)
            const tPopEnd = typeof performance !== 'undefined' ? performance.now() : Date.now();
            const storageConvoIds = ((this._chatStorage.getState() as any).conversations ?? []).map((c: any) => c.id).join(',');
            addTrace('ChatService:populate-done', `convosAdded=${conversationAddedCount} msgsAdded=${messagesAddedCount} storageConvoIds=[${storageConvoIds}] elapsedMs=${(tPopEnd - tPopStart).toFixed(0)}`);
            this.setServiceState({ ...this.serviceState, data: { newMessagesOnly, conversationCount: rawChatStateData.length, latestHandledTimestamp, conversationAddedCount, messagesAddedCount } })
        }
        this.setServiceState({
            ...update.serviceState, data: {
                newMessagesOnly, conversationCount: rawChatStateData.length, latestHandledTimestamp: this.latestHandledTimestamp,
                conversationAddedCount: 0, messagesAddedCount: 0
            }
        })
    }


    disconnect() {
        this.deleteSubscription();
        this.detachPollingStateListener();
    }
    sendTyping({
        isTyping,
        content,
        conversationId,
        userId,
    }: SendTypingServiceParams) {
        // // We send the "typing" signalization using a CustomEvent dispatched to the window object.
        // // It is received in the callback assigned in the constructor
        // // In a real application, instead of dispatching the event here,
        // // you will implement sending signalization to your chat server.
        // const typingEvent = new CustomEvent("chat-protocol", {
        //     detail: {
        //         type: "typing",
        //         isTyping,
        //         content,
        //         conversationId,
        //         userId,
        //         sender: this,
        //     },
        // });
    }
    on<T extends ChatEventType, H extends ChatEvent<T>>(
        evtType: T,
        evtHandler: ChatEventHandler<T, H>
    ) {
        const key = `on${evtType.charAt(0).toUpperCase()}${evtType.substring(1)}`;
        //console.info('ON', key)
        if (key in this.eventHandlers) {
            this.eventHandlers[key] = evtHandler;
        }
    }

    off<T extends ChatEventType, H extends ChatEvent<T>>(
        evtType: T,
        eventHandler: ChatEventHandler<T, H>
    ) {
        //console.info('OFF', evtType)
        if (evtType === 'message') {
            this.eventHandlers.onMessage = () => { };
        }
    }
}





export type StorageUpdateHooks = {
    convoHook: (rawConvo: RawConversationOptionalIdMessagesTS) => Conversation | undefined
    newMessageHook: (message: ChatMessage<MessageContentType.TextPlain, undefined> | ChatMessage<MessageContentType.TextHtml, undefined>, matchedConvoId: string) => void// (rawMsg: RawMessage) => void
}

export type PopulateStateMethod = (chatStateUpdate: ChatStateUpdate) => { conversationAddedCount: number, messagesAddedCount: number }
export const GetPopulateStateMethod: (_chatStorage: IStorage, storageUpdateHooks?: StorageUpdateHooks | undefined, conversationFilter?: (convoId: number) => boolean | undefined) => PopulateStateMethod = (_chatStorage, storageUpdateHooks, conversationFilter) => {
    return (chatStateUpdate: ChatStateUpdate) => {
        const processConvoForUsers = (rawConvo: RawConversationOptionalId) => {
            if (!rawConvo?.id) {
                return
            }
            if (conversationFilter) {
                if (!conversationFilter(rawConvo.id)) {
                    return
                }
            }
            rawConvo.members.forEach((m: any) => {
                const [user] = _chatStorage.getUser(m.id.toString());
                if (!user) {
                    const newUser = new User({
                        id: m.id.toString(),
                        presence: new Presence({ status: UserStatus.Available, description: "" }),
                        //firstName: "",
                        //lastName: "",
                        username: m.name_public,
                        avatar:
                            m.id === 0//this.userId
                                ? "https://www.polygonhealth.com/map/static/media/logo.5b654776c368b37500be.png"
                                : "https://chatscope.io/storybook/react/assets/emily-xzL8sDL2.svg",
                        bio: ""
                    });
                    _chatStorage.addUser(newUser);
                }
            });
        }

        const { userId, rawChatStateData: result, newMessagesOnly } = chatStateUpdate
        var conversationAddedCount = 0
        var messagesAddedCount = 0
        result.forEach((rawConvo: RawConversationOptionalIdMessagesTS) => {
            if (!rawConvo?.id) {
                return
            }
            if (conversationFilter) {
                if (!conversationFilter(rawConvo.id)) {
                    return
                }
            }
            const state = _chatStorage.getState()
            var existingConvo
            if (storageUpdateHooks?.convoHook) {
                existingConvo = storageUpdateHooks?.convoHook(rawConvo)
            }
            // Only fall back to ID matching if hook didn't return a result
            if (!existingConvo) {
                const [existingConvoFoundById] = rawConvo.id ? _chatStorage.getConversation(rawConvo.id.toString()) : [undefined];
                existingConvo = existingConvoFoundById
            }
            processConvoForUsers(rawConvo)
            if (!existingConvo) {
                existingConvo = CreateConversationFromRaw(rawConvo, !newMessagesOnly) //Only update conversation with unreadcount on initial retrieve; dont update on new messages because onMessage calls will increment counter and double the count
                _chatStorage.addConversation(existingConvo);
                //if( !newMessagesOnly){
                //this.updateState()
                conversationAddedCount++
                //}
            }
            if (!existingConvo) {
                console.error(`Unable to find or create a convo`)
                return;
            }
            for (let i = 0; i < rawConvo.messages.length; i++) {

                try {
                    const rawMsg = rawConvo.messages[i]
                    //todo the raw message should have a type text vs html
                    //if (this._chatStorage.getState().activeConversation?.id === rawConvo.id.toString()) {
                    const message = CreateMessageFromRaw(rawMsg, userId, MessageContentType.TextPlain)//TextHtml )// ⬅️ The runtime value determines the content shape
                    //const [existingConvo, count] = this._chatStorage.getConversation(rawConvo.id.toString());
                    const [user] = _chatStorage.getUser(message.sender.id);
                    if (newMessagesOnly) {
                        //     //if ( this._chatStorage.getState().messages[rawConvo.id.toString()])
                        //     console.info("new message", message)
                        //     //TODO:improve by sending the sentMessagesIds array to the SQL function not to even retrieve
                        //     if (//rawMsg.originator_id === this.userId && // to only add messages to the ??????????
                        //         !this.sentMessagesIds[rawMsg.id]) {
                        //         this.eventHandlers.onMessage(new MessageEvent({ message, conversationId: existingConvo?.id!/*rawConvo.id.toString()*/ }));
                        //     }
                        //     const state = this._chatStorage.getState()
                        //     if (rawMsg.originator_id !== this.userId) {
                        //         if (this._chatStorage.getState().activeConversation?.id === rawConvo.id.toString()) {
                        //             const setReadMessageThunk = chatscopeApi.endpoints.setReadMessage.initiate({ messageId: Number(message.id), userId: this.userId });
                        //             this.dispatch(setReadMessageThunk)//.unwrap() // marking messages read one by one // TODO update in the SQL function
                        //         }
                        //     }

                        if (storageUpdateHooks?.newMessageHook) {
                            storageUpdateHooks.newMessageHook(message, existingConvo?.id)//rawConvo.id)//rawMsg)
                        }
                    } else {
                        _chatStorage.addMessage(message, existingConvo?.id/*rawConvo.id.toString()*/, false)
                    }
                    messagesAddedCount++

                } catch (err) {
                    //throw err
                    console.error(printError(err))
                }
            }
        })
        return { conversationAddedCount, messagesAddedCount }
    }
}



/**
 * Counts total unread messages across all conversations in Chatscope storage.
 *
 * Unread = message.status !== MessageStatus.Seen
 */
export function countTotalUnreadMessages(storage: IStorage): number {
    const state = storage.getState();
    const totalUnreadCount = state.conversations.reduce( ( previousValue, c ) => { return previousValue + c.unreadCounter }, 0)
    return totalUnreadCount
}
    // let unreadCount = 0;

    // for (const conversationId in state.messages) {
    //     const messageGroups = state.messages[conversationId];

    //     if (!Array.isArray(messageGroups)) continue;

    //     for (const group of messageGroups) {
    //         if (!Array.isArray(group.messages)) continue;

    //         for (const message of group.messages) {
    //             if (message.status !== MessageStatus.Seen) {
    //                 unreadCount++;
    //             }
    //         }
    //     }
    // }

    //return unreadCount;
// }